import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings2, Sparkles, Workflow } from 'lucide-react';
import { ErrorState } from '@/components/shared/page';
import { ObjectIndex, FilterPill, AddButton } from '@/components/shared/object-index';
import { useT } from '@/lib/i18n';
import { Can } from '@/components/shared/can';
import { Avatar, Skeleton } from '@/components/ui/misc';
import { useDebounce } from '@/hooks/use-debounce';
import { timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useLeadBoard, useMoveLead, usePipelines } from './api';
import { LEAD_SOURCE_LABELS } from './labels';
import { LeadDialog } from './lead-dialog';
import { IncomingColumn } from './incoming-column';
import { ConvertLeadDialog } from './convert-lead-dialog';
import type { Lead, LeadBoardColumn } from './types';

/**
 * The lead board.
 *
 * The same Kanban as the deal pipeline, on the lead pipelines — a lead is
 * answered, chased and qualified long before it has a value or a close date,
 * so the columns are different and so is what a card shows.
 */
export function LeadsBoardPage() {
  const t = useT();
  const { data: pipelines = [] } = usePipelines('lead');
  const [pipelineId, setPipelineId] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Lead | null>(null);
  const [converting, setConverting] = useState<Lead | null>(null);
  const [dragging, setDragging] = useState<Lead | null>(null);

  const debounced = useDebounce(search, 300);
  const { data, isLoading, error, refetch } = useLeadBoard({
    pipelineId: pipelineId || undefined,
    ownerId: ownerFilter || undefined,
    q: debounced || undefined,
  });
  const move = useMoveLead();

  const onDrop = (column: LeadBoardColumn) => {
    const lead = dragging;
    setDragging(null);
    if (!lead || lead.stageId === column.stage.id) return;
    move.mutate({ id: lead.id, stageId: column.stage.id });
  };

  return (
    <div>
      <ObjectIndex
        title="Lead pipeline"
        count={data?.columns.reduce((sum, column) => sum + column.count, 0)}
        mode="board"
        tableHref="/crm/leads"
        boardHref="/crm/leads/board"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search leads"
        filters={
          <>
            <FilterPill
              label="Board"
              value={pipelineId}
              onChange={setPipelineId}
              options={pipelines.map((pipeline) => ({
                value: pipeline.id,
                label: `${pipeline.name}${pipeline.isDefault ? ` (${t('default')})` : ''}`,
              }))}
            />
            <FilterPill
              label="Lead owner"
              value={ownerFilter}
              onChange={setOwnerFilter}
              options={owners(data?.columns).map((owner) => ({ value: owner.id, label: owner.name }))}
            />
          </>
        }
        hasFilters={Boolean(pipelineId || ownerFilter)}
        onClearFilters={() => {
          setPipelineId('');
          setOwnerFilter('');
        }}
        toolbarEnd={
          <>
            <Can permission="admin.workflow.view">
              <Link
                to="/crm/digital-pipeline/lead"
                title={t('Digital pipeline')}
                className="flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <Workflow className="h-4 w-4" />
              </Link>
            </Can>
            <Can permission="crm.pipeline.create">
              <Link
                to="/crm/pipelines/lead"
                title={t('Edit pipeline stages')}
                className="flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <Settings2 className="h-4 w-4" />
              </Link>
            </Can>
          </>
        }
        actions={
          <Can permission="crm.lead.create">
            <AddButton onClick={() => setCreating(true)} label="Add lead" />
          </Can>
        }
      >
        {error ? (
          <div className="p-4">
            <ErrorState message="Could not load the lead board." onRetry={() => void refetch()} />
          </div>
        ) : isLoading ? (
          <div className="flex gap-3 overflow-x-auto p-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-96 w-[calc(100%-2.5rem)] shrink-0 md:w-[17rem]" />
            ))}
          </div>
        ) : (
          <div className="scrollbar-thin flex snap-x snap-mandatory gap-3 overflow-x-auto bg-muted/40 p-3 md:snap-none">
            {data && <IncomingColumn leads={data.incoming ?? []} onOpen={setEditing} />}
            {data?.columns.map((column) => (
              <div
                key={column.stage.id}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => onDrop(column)}
                className={cn(
                  'flex max-h-[calc(100dvh-17rem)] min-h-[24rem] w-[calc(100%-2.5rem)] shrink-0 snap-start flex-col rounded-lg bg-muted/70 transition-colors md:w-[17rem]',
                  dragging && dragging.stageId !== column.stage.id && 'bg-primary/[0.07] ring-2 ring-primary/30',
                )}
              >
                <div className="flex items-center gap-2 px-2.5 pb-2 pt-2.5">
                  <span className="truncate rounded px-2 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: column.stage.colour }}>
                    {column.stage.name}
                  </span>
                  <span className="tabular text-xs font-semibold text-muted-foreground">{column.count}</span>
                </div>

                <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto px-2 pb-2">
                  {column.leads.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      onOpen={() => setEditing(lead)}
                      onConvert={() => setConverting(lead)}
                      onDragStart={() => setDragging(lead)}
                      onDragEnd={() => setDragging(null)}
                    />
                  ))}
                  {column.leads.length === 0 && (
                    <p className="rounded-md border border-dashed px-2 py-6 text-center text-xs text-muted-foreground">
                      {t('Drag leads here')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ObjectIndex>

      {creating && <LeadDialog onClose={() => setCreating(false)} />}
      {editing && <LeadDialog lead={editing} onClose={() => setEditing(null)} />}
      {converting && (
        <ConvertLeadDialog lead={converting} onClose={() => setConverting(null)} />
      )}
    </div>
  );
}

function LeadCard({
  lead,
  onOpen,
  onConvert,
  onDragStart,
  onDragEnd,
}: {
  lead: Lead;
  onOpen: () => void;
  onConvert: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      className="group cursor-grab rounded-md border bg-card p-3 shadow-card transition-all hover:border-foreground/20 hover:shadow-md active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-link line-clamp-2 text-sm font-semibold group-hover:underline">{lead.name}</span>
        <span className="shrink-0 text-[10px] text-muted-foreground">{lead.number}</span>
      </div>
      {lead.companyName && (
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{lead.companyName}</p>
      )}
      {(lead.phone || lead.email) && (
        <p className="mt-1 truncate text-xs text-muted-foreground"><span dir="ltr">{lead.phone ?? lead.email}</span></p>
      )}
      <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
        <span className="truncate">{LEAD_SOURCE_LABELS[lead.source] ?? lead.source}</span>
        <span className="ms-auto shrink-0">{timeAgo(lead.updatedAt)}</span>
      </div>
      <div className="mt-2.5 flex items-center gap-2 border-t pt-2">
        <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
          <Avatar name={lead.owner?.name ?? '?'} size={18} />
          <span className="truncate">{lead.owner?.name ?? 'No owner'}</span>
        </span>
        {lead.status !== 'CONVERTED' && (
          <Can permission="crm.lead.update">
            <button
              onClick={(event) => {
                event.stopPropagation();
                onConvert();
              }}
              className="ms-auto flex shrink-0 items-center gap-1 text-[11px] font-semibold text-link hover:underline"
            >
              <Sparkles className="h-3 w-3" />
              Convert
            </button>
          </Can>
        )}
      </div>
    </div>
  );
}

/** The owners with leads on this board, for the filter. */
function owners(columns: LeadBoardColumn[] | undefined) {
  const found = new Map<string, { id: string; name: string }>();
  for (const column of columns ?? []) {
    for (const lead of column.leads) {
      if (lead.owner) found.set(lead.owner.id, { id: lead.owner.id, name: lead.owner.name });
    }
  }
  return [...found.values()].sort((a, b) => a.name.localeCompare(b.name));
}
